import * as React from "react";

// // // //

/**
 * useScrollThreshold
 * Calls `onThresholdCrossed` when the user scrolls the page past `thresholdPercent`
 * of its total scrollable height. Fires once per downward crossing - scrolling back
 * above the threshold re-arms it.
 *
 * NOTE - only reacts to scroll events, so it does not fire on mount if the page
 * is already past the threshold.
 */
export function useScrollThreshold(props: {
    /** Percent (0-100) of the page's scrollable height */
    thresholdPercent: number;
    onThresholdCrossed: () => void;
}) {
    const { thresholdPercent, onThresholdCrossed } = props;

    // Hold the latest callback in a ref so callers don't need to memoize it
    const callbackRef = React.useRef(onThresholdCrossed);
    React.useEffect(() => {
        callbackRef.current = onThresholdCrossed;
    }, [onThresholdCrossed]);

    React.useEffect(() => {
        let isCrossed = false;

        const handleScroll = () => {
            const scrollableHeight =
                document.documentElement.scrollHeight - window.innerHeight;
            if (scrollableHeight <= 0) {
                return;
            }

            const scrollPercent = (window.scrollY / scrollableHeight) * 100;
            const nextIsCrossed = scrollPercent > thresholdPercent;

            if (nextIsCrossed && !isCrossed) {
                callbackRef.current();
            }
            isCrossed = nextIsCrossed;
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [thresholdPercent]);
}
